import React, { useEffect, useState } from 'react';
import { AlertCircleIcon, BellOffIcon, BellRingIcon, Loader2Icon, SmartphoneIcon } from 'lucide-react';
import { useAdminData } from '../../contexts/AdminDataContext';
import { defaultPrefs, savePrefs, subscribePrefs } from '../../services/adminPrefsService';
import type { NotificationPrefs } from '../../services/adminPrefsService';
import { checkPushStatus, enablePush } from '../../services/pushService';
import type { PushCheck, PushStatus } from '../../services/pushService';
import { debugErrorMessage, logFirebaseError } from '../../lib/firebase';
const options: {
  key: keyof NotificationPrefs;
  label: string;
  text: string;
}[] = [{
  key: 'hoursReminder',
  label: 'Husk at registrere timer',
  text: 'Én påmindelse kl. 15.30, hvis du har en aktiv sag i dag uden registrerede timer.'
}, {
  key: 'timerReminder',
  label: 'Påmind mig når en timer stadig kører',
  text: 'Efter 2 timer og derefter hver 2. time. Timeren stoppes aldrig automatisk.'
}];
const statusInfo: Record<Exclude<PushStatus, 'checking'>, {
  title: string;
  text: string;
  icon: typeof BellRingIcon;
  tone: string;
}> = {
  active: {
    title: 'Push-notifikationer er aktive på denne enhed',
    text: 'Denne enhed modtager påmindelser.',
    icon: BellRingIcon,
    tone: 'text-status-ok'
  },
  'permission-missing': {
    title: 'Notifikationstilladelse mangler',
    text: 'Slå en af påmindelserne herunder til, så beder enheden om tilladelse.',
    icon: BellOffIcon,
    tone: 'text-status-warn'
  },
  'permission-denied': {
    title: 'Notifikationstilladelse mangler',
    text: 'Notifikationer er afvist for LYKKEVÆRK Admin. Tillad dem i enhedens indstillinger (på iPhone: Indstillinger → Notifikationer → LYKKEVÆRK). Påmindelserne vises stadig inde i appen.',
    icon: BellOffIcon,
    tone: 'text-status-warn'
  },
  'install-required': {
    title: 'Installer LYKKEVÆRK Admin på hjemmeskærmen og åbn appen derfra for at aktivere push-notifikationer.',
    text: 'Tryk Del → Føj til hjemmeskærm, åbn appen fra hjemmeskærmen, og slå påmindelserne til her.',
    icon: SmartphoneIcon,
    tone: 'text-status-warn'
  },
  unsupported: {
    title: 'Push understøttes ikke på denne enhed',
    text: 'Påmindelserne vises i stedet inde i appen — timerbjælken og "Kræver opmærksomhed" på I dag.',
    icon: AlertCircleIcon,
    tone: 'text-ink-muted'
  },
  'registration-failed': {
    title: 'Enheden kunne ikke tilmeldes push',
    text: 'Tilladelsen er givet, men tilmeldingen fejlede. Prøv igen — påmindelserne vises imens inde i appen.',
    icon: AlertCircleIcon,
    tone: 'text-danger'
  }
};
export function NotificationsSection() {
  const {
    user
  } = useAdminData();
  const [prefs, setPrefs] = useState<NotificationPrefs>(defaultPrefs);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<keyof NotificationPrefs | null>(null);
  const [push, setPush] = useState<PushCheck>({
    status: 'checking'
  });
  const status = push.status;
  const [error, setError] = useState('');
  useEffect(() => {
    if (!user) return;
    return subscribePrefs(user.uid, next => {
      setPrefs(next);
      setLoading(false);
    }, caught => {
      logFirebaseError('onSnapshot adminPrefs', caught);
      setLoading(false);
      setError(debugErrorMessage('adminPrefs', caught));
    });
  }, [user]);

  // Status tjekkes uden at spørge om tilladelse; et eksisterende token fornyes stille.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    checkPushStatus(user.uid).then(next => {
      if (!cancelled) setPush(next);
    });
    return () => {
      cancelled = true;
    };
  }, [user]);
  async function toggle(key: keyof NotificationPrefs) {
    if (!user || busy) return;
    const next = !prefs[key];
    setBusy(key);
    setError('');
    try {
      await savePrefs(user.uid, {
        [key]: next
      });
      if (next) {
        // Tilladelse bedes der først om her — når admin selv slår det til.
        setPush(await enablePush(user.uid));
      }
    } catch (caught) {
      logFirebaseError('savePrefs', caught);
      setError(debugErrorMessage('savePrefs', caught));
    }
    setBusy(null);
  }
  const info = status === 'checking' ? null : statusInfo[status];
  return <section className="mt-5 max-w-2xl rounded-card border border-line bg-white p-6" aria-labelledby="notifikationer-titel" data-id="element-263" data-mp="1yluzmpq9x5o0">
      <h2 id="notifikationer-titel" className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-264" data-mp="1kugjqrrnxkl6">
        Notifikationer
      </h2>
      <p className="mt-1.5 text-sm text-ink-muted" data-id="element-265" data-mp="1pmrjnumel6dq">Kun påmindelser om timer. Valgene følger din bruger.</p>

      <div role="status" aria-live="polite" className="mt-4 flex items-start gap-3 rounded-xl border border-line px-4 py-3" data-id="element-266" data-mp="1aufwiuqoakbp">
        {info ? <>
            <info.icon className={`mt-0.5 h-5 w-5 shrink-0 ${info.tone}`} aria-hidden="true" data-id="element-267" data-mp="11wx8dhhqfpvl" />
            <div className="min-w-0" data-id="element-268" data-mp="15weco7lfagz6">
              <p className="text-[15px] font-medium text-ink" data-id="element-269" data-mp="15zjzfg6kx6rq">{info.title}</p>
              <p className="mt-0.5 text-sm text-ink-muted" data-id="element-270" data-mp="n9oz4m7zhum9">{info.text}</p>
              {status !== 'active' && push.detail && <p className="mt-1 break-words text-xs text-ink-faint" data-id="element-271" data-mp="icf3gou04mpz">Detalje: {push.detail}</p>}
              {status === 'registration-failed' && push.diagnostics && <dl className="mt-2 grid grid-cols-[auto,1fr] gap-x-3 gap-y-0.5 break-all text-xs text-ink-faint" data-id="element-272" data-mp="rcv5dtv3jlsq">
                  <dt data-id="element-273" data-mp="118y4srykd6bz">Script</dt>
                  <dd data-id="element-274" data-mp="119pz7zgfcy0o">{push.diagnostics.scriptUrl}</dd>
                  <dt data-id="element-275" data-mp="1173u06h0aulm">Scope</dt>
                  <dd data-id="element-276" data-mp="1f0urgghgqo0z">{push.diagnostics.activeScope ?? push.diagnostics.scope}</dd>
                  <dt data-id="element-277" data-mp="1ayq4u1pfmrnu">HTTP-status</dt>
                  <dd data-id="element-278" data-mp="1azssm0rgkdzj">{push.diagnostics.httpStatus ?? 'ikke hentet'}</dd>
                  <dt data-id="element-279" data-mp="1oxzhirefj3n2">Indhold</dt>
                  <dd data-id="element-280" data-mp="1ozam2tfp8ff8">
                    {push.diagnostics.scriptKind === 'javascript' ? 'JavaScript' : push.diagnostics.scriptKind === 'html' ? 'HTML-side (forkert)' : 'ukendt'}
                    {push.diagnostics.contentType ? ` · ${push.diagnostics.contentType}` : ''}
                  </dd>
                  <dt data-id="element-281" data-mp="qln3qjwhh7c2">HTTPS</dt>
                  <dd data-id="element-282" data-mp="qklixnpo16h5">{push.diagnostics.https ? 'ja' : 'nej'}</dd>
                  <dt data-id="element-283" data-mp="veg8h7rycfo5">Installeret app</dt>
                  <dd data-id="element-284" data-mp="vf9a5uc20ezf">{push.diagnostics.standalone ? 'ja' : 'nej'}</dd>
                  {push.diagnostics.registrationError && <>
                      <dt data-id="element-285" data-mp="15aj8o838u48g">Fejl</dt>
                      <dd data-id="element-286" data-mp="15dp03tzchi7c">{push.diagnostics.registrationError}</dd>
                    </>}
                </dl>}
              {status === 'registration-failed' && user && <button type="button" onClick={async () => {
            setPush({
              status: 'checking'
            });
            setPush(await enablePush(user.uid));
          }} className="mt-2 text-sm font-medium text-accent transition-colors duration-150 hover:text-accent-deep" data-id="element-287" data-mp="1e9wodihynp1m">
                  Prøv igen
                </button>}
            </div>
          </> : <p className="flex items-center gap-2 text-sm text-ink-muted" data-id="element-288" data-mp="2yuutd9r092hm">
            <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-289" data-mp="2tw9nouszhqls" />
            Tjekker push på denne enhed …
          </p>}
      </div>

      <ul className="mt-2 divide-y divide-line" data-id="element-290" data-mp="3mjw453nb8pq2">
        {options.map(option => <li key={option.key} className="flex items-center justify-between gap-4 py-3.5" data-id="element-291" data-mp="3mht73znqpwbu">
            <div className="min-w-0" data-id="element-292" data-mp="3hmvxaqimzixw">
              <p id={`notif-${option.key}`} className="text-[15px] font-medium text-ink" data-id="element-293" data-mp="3djqwcse7fpbd">
                {option.label}
              </p>
              <p className="mt-0.5 text-sm text-ink-muted" data-id="element-294" data-mp="26779xy1h0hwk">{option.text}</p>
            </div>
            <button type="button" role="switch" aria-checked={prefs[option.key]} aria-labelledby={`notif-${option.key}`} disabled={loading || busy !== null} onClick={() => toggle(option.key)} className={`relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-colors duration-200 ease-smooth disabled:opacity-60 ${prefs[option.key] ? 'bg-accent' : 'bg-canvas-deep'}`} data-id="element-295" data-mp="2239blpjyzb4k">
              <span className={`inline-block h-5 w-5 rounded-full bg-ink shadow transition-transform duration-200 ease-smooth ${prefs[option.key] ? 'translate-x-6' : 'translate-x-1'}`} aria-hidden="true" data-id="element-296" data-mp="oti5roeonoxh" />
            </button>
          </li>)}
      </ul>

      {error && <p role="alert" className="mt-3 flex items-start gap-2 text-sm text-danger" data-id="element-297" data-mp="17phrihh2hw8m">
          <AlertCircleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-298" data-mp="1ci1gsbp4aft9" />
          {error}
        </p>}
    </section>;
}